import { html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { ButtressDbService, buttressDbServiceContext } from '@buttress/crag';

import { NSDirectoryService } from '../../ns-services/ns-directory-service.js';
import { LtnAuthService } from '../../ltn-auth/ltn-auth-service.js';
import { ltnAuthServiceContext, nsDirectoryServiceContext } from '../../ns-services/contexts.js';
import { searchPage } from '../../helpers/buttress-search.js';
import { renderFormField } from '../../helpers/form-field.js';
import { nsFormFieldStyles, nsSharedStyles } from '../../ns-styles/index.js';
import type { DataRecord, DataSet, GrantLevel } from '../../models/company-data.js';
import { loadSets } from './data-loaders.js';

// shortcut: lists the first 200 records by title, as the reference picker does; swap for a searching picker once sets get large
const RECORD_OPTION_LIMIT = 200;

export interface ShareChoice {
  setId: string;
  recordId?: string;
  level: GrantLevel;
}

// Which of the company's data sets (or one record in it) to share, and at what level
@customElement('data-share-picker')
export class DataSharePicker extends LitElement {
  @property({ type: String, attribute: 'id-prefix' })
  idPrefix = 'share';

  @consume({ context: buttressDbServiceContext })
  private __db?: ButtressDbService;

  @consume({ context: nsDirectoryServiceContext })
  private __directory?: NSDirectoryService;

  @consume({ context: ltnAuthServiceContext })
  private __auth?: LtnAuthService;

  @state()
  private __sets: DataSet[] = [];

  @state()
  private __records: DataRecord[] = [];

  @state()
  private __setId = '';

  @state()
  private __recordId = '';

  @state()
  private __level: GrantLevel = 'READ';

  static styles = [nsSharedStyles, nsFormFieldStyles];

  async connectedCallback() {
    super.connectedCallback();
    await this.__directory?.initialised;
    const companyId = this.__directory?.company?.id;
    if (!this.__db || !companyId) return;
    this.__sets = (await loadSets(this.__db, companyId))
      .filter((set) => set.status === 'ACTIVE')
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  get choice(): ShareChoice | undefined {
    if (!this.__setId) return undefined;
    return this.__recordId
      ? { setId: this.__setId, recordId: this.__recordId, level: 'READ' }
      : { setId: this.__setId, level: this.__level };
  }

  private async __chooseSet(setId: string) {
    this.__setId = setId;
    this.__recordId = '';
    this.__records = setId
      ? (
          await searchPage<DataRecord>('data-record', this.__auth?.token, {
            query: { dataSetId: { $eq: setId } },
            limit: RECORD_OPTION_LIMIT,
            skip: 0,
            sort: { title: 1, _id: 1 },
          })
        ).results
      : [];
  }

  render() {
    const id = (name: string) => `${this.idPrefix}-${name}`;
    if (this.__sets.length === 0) {
      return html`
        <p>Your company has no data sets to share yet.</p>
      `;
    }
    return html`
      ${renderFormField({
        id: id('set'),
        label: 'Data set',
        control: html`
          <select id=${id('set')} @change=${(e: Event) => this.__chooseSet((e.target as HTMLSelectElement).value)}>
            <option value="" ?selected=${!this.__setId}>Choose a data set</option>
            ${this.__sets.map(
              (set) => html`
                <option value=${set.id} ?selected=${this.__setId === set.id}>${set.name}</option>
              `,
            )}
          </select>
        `,
      })}
      ${this.__setId
        ? renderFormField({
            id: id('record'),
            label: 'What to share',
            control: html`
              <select
                id=${id('record')}
                @change=${(e: Event) => (this.__recordId = (e.target as HTMLSelectElement).value)}
              >
                <option value="" ?selected=${!this.__recordId}>The whole data set</option>
                ${this.__records.map(
                  (record) => html`
                    <option value=${record.id} ?selected=${this.__recordId === record.id}>
                      One record: ${record.title || 'Untitled'}
                    </option>
                  `,
                )}
              </select>
            `,
          })
        : nothing}
      ${this.__setId && !this.__recordId
        ? renderFormField({
            id: id('level'),
            label: 'Access',
            hint: 'Either way they never see private fields, and never change or delete your records.',
            control: html`
              <select
                id=${id('level')}
                @change=${(e: Event) => (this.__level = (e.target as HTMLSelectElement).value as GrantLevel)}
              >
                <option value="READ" ?selected=${this.__level === 'READ'}>Read</option>
                <option value="CONTRIBUTE" ?selected=${this.__level === 'CONTRIBUTE'}>Read and add records</option>
              </select>
            `,
          })
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'data-share-picker': DataSharePicker;
  }
}
